import { AlertTriangle, Info, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  fromMarkdown,
  toMarkdown,
  ResumeMarkdownError,
  type OptimizedResumeDocument,
} from "@super-resume-new/resume-engine";
import { Button } from "@super-resume-new/ui/components/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "@super-resume-new/ui/components/dialog";
import { Textarea } from "@super-resume-new/ui/components/textarea";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  doc: OptimizedResumeDocument;
  onSave: (doc: OptimizedResumeDocument) => void;
  saving?: boolean;
};

type Check =
  | { ok: true; doc: OptimizedResumeDocument; warnings: string[] }
  | { ok: false; error: string; line?: number };

function check(text: string, original: OptimizedResumeDocument): Check {
  try {
    const { doc, warnings } = fromMarkdown(text, original);
    return { ok: true, doc, warnings };
  } catch (err) {
    if (err instanceof ResumeMarkdownError) return { ok: false, error: err.message, line: err.line };
    return { ok: false, error: err instanceof Error ? err.message : "Could not read this resume." };
  }
}

export function ResumeEditorDialog({ open, onOpenChange, doc, onSave, saving }: Props) {
  const original = useMemo(() => toMarkdown(doc), [doc]);
  const [text, setText] = useState(original);

  // Reopening after a save must show the saved resume, not the stale draft.
  useEffect(() => {
    if (open) setText(original);
  }, [open, original]);

  // Cheap enough to run on every keystroke — it is a few thousand characters of
  // line splitting, no debounce needed, and the feedback stays instant.
  const result = check(text, doc);
  const dirty = text !== original;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit resume</DialogTitle>
          <DialogDescription>
            Change the text below, then save to re-render the PDF.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 px-6 pb-2">
          <div className="flex items-start gap-2 rounded-xl border border-violet-500/30 bg-violet-500/[0.08] p-3 text-xs leading-relaxed text-muted-foreground">
            <Info className="mt-0.5 size-4 shrink-0 text-violet-400" aria-hidden="true" />
            <span>
              Intended for light edits — fix a word, drop a bullet, add a line. Keep the{" "}
              <code className="font-mono text-[11px] text-foreground">#</code>,{" "}
              <code className="font-mono text-[11px] text-foreground">##</code>,{" "}
              <code className="font-mono text-[11px] text-foreground">**Label:**</code> and{" "}
              <code className="font-mono text-[11px] text-foreground">-</code> markers as they are.
              Large rewrites can break the layout.
            </span>
          </div>

          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
            aria-label="Resume source text"
            aria-invalid={!result.ok}
            className="h-[420px] resize-none font-mono text-xs leading-relaxed"
          />

          {!result.ok && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/[0.06] p-3 text-xs font-medium leading-relaxed text-destructive"
            >
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>
                {result.line != null && `Line ${result.line}: `}
                {result.error} Fix it, or undo your changes to start over.
              </span>
            </div>
          )}

          {result.ok && result.warnings.length > 0 && (
            <div
              role="status"
              className="flex flex-col gap-1 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs leading-relaxed text-amber-500"
            >
              {result.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setText(original)}
            disabled={!dirty || saving}
            className="gap-1.5"
          >
            <RotateCcw className="size-3.5" aria-hidden="true" />
            Undo changes
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => result.ok && onSave(result.doc)}
            disabled={!result.ok || !dirty || saving}
            loading={saving}
          >
            {saving ? "Saving…" : "Save and re-render"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
